import { GitBranchIcon, ListIcon, RefreshCwIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { fetchConnectedSlugs, resolveComposioUserId } from "../../composioConnect";
import { useI18n } from "~/i18n";
import { fetchMemories, type MemoryScope, type MorrowMemory } from "../../morrowMemory";
import type { OrgMember } from "../../orgMembers";
import { useMorrowClerkAuth } from "../clerk/morrowClerkAuth";
import { MorrowMemoryListSkeleton } from "../morrow/MorrowPageSkeletons";
import { Button } from "../ui/button";
import {
  isSelfMember,
  resolveMemberPersonAvatar,
  resolveSelfAvatarSeed,
} from "../workspace/morrowMemberAvatarResolve";
import { resolvePersonAvatarUrl } from "../workspace/morrowPersonAvatarCatalog";
import { useUserProfile } from "../workspace/morrowUserProfileStore";
import { MemoryConstellation, type ConstellationSelection } from "./MemoryConstellation";
import { MemoryList } from "./MemoryList";
import { FACT_TYPE_META, factTypeColor } from "./forceGraphModel";

export function MorrowMemoryPanel({
  scope,
  rootName,
  members = [],
  defaultViewMode = "graph",
  rootLogoUrl,
  selfEmail,
}: {
  readonly scope: Exclude<MemoryScope, "all">;
  readonly rootName: string;
  readonly rootLogoUrl?: string;
  readonly members?: readonly OrgMember[];
  readonly defaultViewMode?: "graph" | "list";
  readonly selfEmail?: string | undefined;
}) {
  const { t } = useI18n();
  const profile = useUserProfile();
  const { isLoaded: clerkLoaded, isSignedIn, userId: clerkUserId } = useMorrowClerkAuth();
  const composioUserId = resolveComposioUserId(clerkLoaded && isSignedIn ? clerkUserId : null);
  const [memories, setMemories] = useState<readonly MorrowMemory[]>([]);
  const [connectedTools, setConnectedTools] = useState<readonly string[]>([]);
  const [configured, setConfigured] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<"graph" | "list">(defaultViewMode);
  const [sel, setSel] = useState<ConstellationSelection | null>(null);

  const resolvePersonAvatar = useCallback(
    (member: OrgMember) => {
      const name = member.name?.trim() || member.email;
      const isSelf = isSelfMember(member, clerkUserId, selfEmail);
      const { avatarSeed } = resolveMemberPersonAvatar({
        email: member.email,
        userId: member.userId,
        name,
        avatarSeed: member.avatarSeed,
        isSelf,
        ...(isSelf ? { selfAvatarSeed: resolveSelfAvatarSeed(profile.avatarSeed, name) } : {}),
      });
      return resolvePersonAvatarUrl(name, {
        ...(avatarSeed ? { avatarSeed } : {}),
      });
    },
    [clerkUserId, profile.avatarSeed, selfEmail],
  );

  const load = useCallback(async () => {
    setLoading(true);
    const [memoryPayload, tools] = await Promise.all([
      fetchMemories("", scope),
      composioUserId ? fetchConnectedSlugs(composioUserId) : Promise.resolve([]),
    ]);
    setMemories(memoryPayload.memories);
    if (tools) setConnectedTools(tools); // null = unknown/failed → keep prior set
    setConfigured(memoryPayload.configured);
    setError(memoryPayload.error);
    setLoading(false);
  }, [composioUserId, scope]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!configured) {
    return (
      <div className="rounded-lg border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
        {t("memory.subtitleUnconfigured")}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {scope === "org" ? t("memory.subtitleOrg") : t("memory.subtitlePersonal")}
        </p>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            <Button
              type="button"
              size="sm"
              variant={viewMode === "graph" ? "default" : "outline"}
              onClick={() => setViewMode("graph")}
            >
              <GitBranchIcon className="size-3.5" aria-hidden="true" />
              {t("memory.view.graph")}
            </Button>
            <Button
              type="button"
              size="sm"
              variant={viewMode === "list" ? "default" : "outline"}
              onClick={() => setViewMode("list")}
            >
              <ListIcon className="size-3.5" aria-hidden="true" />
              {t("memory.view.list")}
            </Button>
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => void load()}
            disabled={loading}
          >
            <RefreshCwIcon className={loading ? "animate-spin" : ""} aria-hidden="true" />
            {loading ? t("common.loading") : t("common.refresh")}
          </Button>
        </div>
      </div>

      {error ? (
        <div className="rounded-lg border border-border bg-muted p-4 text-sm font-medium text-foreground">
          {error}
        </div>
      ) : null}

      {loading ? (
        <MorrowMemoryListSkeleton />
      ) : memories.length === 0 ? (
        <MemoryEmptyState scope={scope} />
      ) : viewMode === "graph" ? (
        <div className="relative h-[72vh] min-h-[520px] w-full">
          <MemoryConstellation
            scope={scope === "org" ? "org" : "personal"}
            rootName={rootName}
            {...(rootLogoUrl ? { rootLogoUrl } : {})}
            members={members}
            memories={memories}
            connectedTools={connectedTools}
            resolvePersonAvatar={resolvePersonAvatar}
            selectedId={sel?.node.id ?? null}
            onSelect={setSel}
          />
          {sel ? <ConstellationCard sel={sel} onClose={() => setSel(null)} /> : null}
        </div>
      ) : (
        <MemoryList memories={memories} />
      )}
    </div>
  );
}

function ConstellationCard({
  sel,
  onClose,
}: {
  readonly sel: ConstellationSelection;
  readonly onClose: () => void;
}) {
  const n = sel.node;
  const m = n.memory;
  const closeBtn = (
    <button
      type="button"
      onClick={onClose}
      className="absolute right-3 top-3 grid size-6 place-items-center rounded-md bg-muted text-muted-foreground hover:text-foreground"
      aria-label="Close"
    >
      ✕
    </button>
  );
  const sl = "mb-1.5 text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground";

  if (n.t === "mem" && m) {
    const related = sel.neighbors.filter((x) => x.t === "mem");
    const topics = sel.neighbors.filter((x) => x.t === "entity");
    const meta = FACT_TYPE_META.find((f) => f.type === (m.type ?? "").trim().toLowerCase());
    const color = meta?.color ?? factTypeColor(m.type);
    const label = meta?.label ?? m.type ?? "Memory";
    return (
      <div className="absolute right-3 top-3 z-10 max-h-[calc(72vh-24px)] w-[340px] overflow-auto rounded-2xl border border-border bg-card p-4 text-sm shadow-xl">
        {closeBtn}
        <span
          className="inline-flex items-center rounded-full px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.07em]"
          style={{ color, background: `${color}18` }}
        >
          {label}
        </span>
        <p className="mt-3 text-[15px] leading-snug text-foreground">{m.text}</p>
        {m.ownerEmail ? (
          <p className="mt-1 text-xs text-muted-foreground">Belongs to {m.ownerEmail}</p>
        ) : null}
        <div className="mt-4">
          <div className={sl}>How it happened</div>
          <div className="rounded-xl border border-border bg-muted/40 p-3 text-xs leading-relaxed text-muted-foreground">
            Captured
            {m.createdAt ? ` on ${new Date(m.createdAt).toLocaleString()}` : ""}
            {m.toolSlug ? ` · from ${m.toolSlug}` : ""}.
            {m.threadId ? " Linked to a conversation." : ""}
          </div>
        </div>
        {topics.length ? (
          <div className="mt-4">
            <div className={sl}>About</div>
            <div className="flex flex-wrap gap-1.5">
              {topics.map((t) => (
                <span
                  key={t.id}
                  className="rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground"
                >
                  {t.name}
                </span>
              ))}
            </div>
          </div>
        ) : null}
        {related.length ? (
          <div className="mt-4">
            <div className={sl}>Related memories</div>
            {related.slice(0, 6).map((r) => (
              <div key={r.id} className="flex gap-2 border-b border-border/60 py-1.5 last:border-0">
                <span
                  className="mt-1.5 size-2 shrink-0 rounded-full"
                  style={{ background: r.color }}
                />
                <span className="min-w-0 text-xs text-foreground">
                  {r.memory?.text ?? r.name}
                  {r.owner ? (
                    <span className="block text-[11px] text-muted-foreground">{r.owner}</span>
                  ) : null}
                </span>
              </div>
            ))}
          </div>
        ) : null}
        <div className="mt-4 flex gap-2">
          <button className="flex-1 rounded-lg border border-border py-1.5 text-xs text-muted-foreground hover:text-foreground">
            Open source chat
          </button>
          <button className="flex-1 rounded-lg border border-red-200 py-1.5 text-xs text-red-600 hover:bg-red-50">
            Forget
          </button>
        </div>
      </div>
    );
  }

  const color = n.color === "#0f172a" ? "#334155" : n.color;
  const kindLabel = FACT_TYPE_META.find((f) => f.type === n.label)?.label;
  const label =
    n.t === "you"
      ? "You"
      : n.t === "person"
        ? "Person"
        : n.t === "app"
          ? "Connected app"
          : n.t === "entity"
            ? "Topic"
            : n.t === "kind"
              ? `${kindLabel ?? "Memory"} cluster`
              : "Company";
  return (
    <div className="absolute right-3 top-3 z-10 max-h-[calc(72vh-24px)] w-[340px] overflow-auto rounded-2xl border border-border bg-card p-4 text-sm shadow-xl">
      {closeBtn}
      <span
        className="inline-flex items-center rounded-full px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.07em]"
        style={{ color, background: `${color}18` }}
      >
        {label}
      </span>
      <p className="mt-3 text-[15px] font-medium leading-snug text-foreground">{n.name ?? label}</p>
      <div className="mt-4">
        <div className={sl}>Connected to</div>
        {sel.neighbors.slice(0, 10).map((k) => (
          <div key={k.id} className="flex gap-2 border-b border-border/60 py-1.5 last:border-0">
            <span
              className="mt-1.5 size-2 shrink-0 rounded-full"
              style={{ background: k.color === "#0f172a" ? "#334155" : k.color }}
            />
            <span className="min-w-0 text-xs text-foreground">
              {k.memory?.text ?? k.name ?? k.t}
              <span className="block text-[11px] text-muted-foreground">{k.t}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MemoryEmptyState({ scope }: { readonly scope: Exclude<MemoryScope, "all"> }) {
  const { t } = useI18n();
  const sections =
    scope === "org"
      ? ([
          {
            titleKey: "memory.sectionWhat",
            hintKey: "memory.sectionWhatHintOrg",
          },
          {
            titleKey: "memory.sectionPurpose",
            hintKey: "memory.sectionPurposeHintOrg",
          },
          {
            titleKey: "memory.sectionCapture",
            hintKey: "memory.sectionCaptureHintOrg",
          },
        ] as const)
      : ([
          {
            titleKey: "memory.sectionWhat",
            hintKey: "memory.sectionWhatHint",
          },
          {
            titleKey: "memory.sectionPurpose",
            hintKey: "memory.sectionPurposeHint",
          },
          {
            titleKey: "memory.sectionCapture",
            hintKey: "memory.sectionCaptureHint",
          },
        ] as const);

  return (
    <section className="flex flex-col gap-8 py-2">
      <div className="min-w-0">
        <h2 className="text-base font-semibold tracking-tight text-foreground">
          {scope === "org" ? t("memory.emptyTitleOrg") : t("memory.emptyTitle")}
        </h2>
        <p className="mt-2 max-w-prose text-sm leading-6 text-muted-foreground">
          {scope === "org" ? t("memory.emptyBodyOrg") : t("memory.emptyBody")}
        </p>
      </div>
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">
          {t("memory.emptySectionsLabel")}
        </h3>
        <ul className="mt-4 grid gap-3 sm:grid-cols-2">
          {sections.map(({ titleKey, hintKey }) => (
            <li
              key={titleKey}
              className="flex min-h-[5.5rem] gap-3 rounded-xl border border-border/60 bg-muted/20 p-4"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">{t(titleKey)}</p>
                <p className="mt-1 text-xs leading-5 text-muted-foreground">{t(hintKey)}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
